import { db } from '@/data/firebase/config';
import { Campaign, CampaignRanking } from '@/domain/types/campaigns';
import { filterAndSortRanking } from '@/domain/use-cases/filter-and-sort-ranking';
import { getDisabledUserIds } from '@/domain/use-cases/get-disabled-user-ids';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { useCallback, useState } from 'react';

const useCampaignDetail = (campaignId: string) => {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [rankings, setRankings] = useState<CampaignRanking[]>([]);
  const [totalRankingsCount, setTotalRankingsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!campaignId) return;
    setIsLoading(true);
    try {
      const [campaignSnap, rankingsSnap] = await Promise.all([
        getDoc(doc(db, 'campaigns', campaignId)),
        getDocs(
          query(
            collection(db, 'campaigns', campaignId, 'rankings'),
            orderBy('mentionCount', 'desc'),
          ),
        ),
      ]);

      if (campaignSnap.exists()) {
        setCampaign({
          id: campaignSnap.id,
          ...(campaignSnap.data() as Omit<Campaign, 'id'>),
        });
      }

      const allRankings = rankingsSnap.docs.map((d) => d.data() as CampaignRanking);
      setTotalRankingsCount(allRankings.length);
      const disabledIds = await getDisabledUserIds(allRankings.map((r) => r.userId));

      setRankings(filterAndSortRanking(allRankings, disabledIds));
    } catch (err) {
      console.error('Error cargando detalle de campaña:', err);
    } finally {
      setIsLoading(false);
    }
  }, [campaignId]);

  const toggleActive = useCallback(async () => {
    if (!campaign) return;
    const newValue = !campaign.isActive;
    await updateDoc(doc(db, 'campaigns', campaignId), { isActive: newValue });
    setCampaign((prev) => (prev ? { ...prev, isActive: newValue } : prev));
  }, [campaign, campaignId]);

  const deleteCampaign = useCallback(async () => {
    const rankingsSnap = await getDocs(
      collection(db, 'campaigns', campaignId, 'rankings'),
    );
    if (!rankingsSnap.empty) {
      const batch = writeBatch(db);
      rankingsSnap.docs.forEach((d) => {
        batch.delete(d.ref);
      });
      await batch.commit();
    }
    await deleteDoc(doc(db, 'campaigns', campaignId));
  }, [campaignId]);

  return {
    campaign,
    rankings,
    totalRankingsCount,
    isLoading,
    fetchDetail,
    toggleActive,
    deleteCampaign,
  };
};

export default useCampaignDetail;
